import React, { useState, useEffect, useRef } from 'react';
import { Route } from 'react-router-dom';
import {
  TableRow,
  Tooltip,
  FormControl,
  InputLabel,
  Checkbox,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Paper,
  Popper,
  Box,
  Pagination,
  CircularProgress,
} from '@mui/material';
import * as E from 'js/components/DesignSystem/Table/styles';
import * as S from './styles';
import { MdContentCopy } from 'react-icons/md';
import searchIcon from 'styles/assets/SearchUser/search.svg';
import placeholder from 'styles/assets/placeholder/placeholder_card.png';
import { firebaseDatabase } from 'js/library/utils/firebaseUtils';
import { updateDatabase } from 'js/library/services/DatabaseManager';
import { downloadImage } from 'js/library/services/StorageManager';
import { getDataListRestApi } from 'js/library/utils/API/restApiFirebase';
import { FunnelSimple, X } from 'phosphor-react';
import { toast } from 'react-toastify';
import usePagination from 'js/components/TriiboComponents/Tables/usePagination';
import CustomButtonsTable from 'js/components/TriiboComponents/Tables/CustomButtonsTable';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const DataTableCards = (props) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [dataTotal, setDataTotal] = useState([]);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [dataSearch, setDataSearch] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [organizations, setOrganizations] = useState({});

  const PER_PAGE = 25;
  const count = Math.ceil(dataSearch.length / PER_PAGE);
  const _DATA = usePagination(dataSearch, PER_PAGE);

  const navigate = useHistory();

  const handleRowClick = (item) => {
    if (item.isDuplicating) {
      return;
    }
    navigate.push({ pathname: `${props.pathName}${item.key}`, state: item });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const dataReceived = await getDataListRestApi('Cards');
        const orgsReceived = await getDataListRestApi('Organizations/');

        const data = Object.keys(dataReceived).map((key) => ({
          ...dataReceived[key],
          key,
          downloadedThumb: placeholder,
        }));

        const transformed = transformData(data);

        setDataTotal(transformed);
        setDataSearch(transformed);
        setOrganizations(orgsReceived);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const downloadedKeys = useRef(new Set());

  useEffect(() => {
    if (!_DATA) return;

    const visibleData = _DATA.currentData();

    visibleData.forEach((item) => {
      const alreadyDownloaded = item.downloadedThumb && item.downloadedThumb !== placeholder;

      if (!alreadyDownloaded && item.images?.mobile && !downloadedKeys.current.has(item.key)) {
        downloadedKeys.current.add(item.key);
        const [bucket, path] = item.images.mobile.split('/');

        downloadImage(bucket, path)
          .then((downloadedImage) => {
            setDataTotal((prev) =>
              prev.map((i) => (i.key === item.key ? { ...i, downloadedThumb: downloadedImage } : i))
            );

            setDataSearch((prev) =>
              prev.map((i) => (i.key === item.key ? { ...i, downloadedThumb: downloadedImage } : i))
            );
          })
          .catch(() => {});
      }
    });
  }, [page, dataSearch]);

  const columns = [
    {
      heading: 'Thumbnail',
      value: 'downloadedThumb',
      render: (item) => (
        <img src={item.downloadedThumb} alt={item.title} style={{ width: '65px' }} />
      ),
    },
    { heading: 'Título', value: 'title' },
    { heading: 'Subtítulo', value: 'subtitle' },
    { heading: 'Keywords', value: 'keyWordsSet' },
    {
      heading: 'Organizações',
      value: 'orgs',
      render: (item) => (item.orgs && item.orgs.length > 0 ? renderOrgs(item.orgs) : '---'),
    },
    {
      heading: 'Prioridade',
      value: 'priority',
      render: (item) => (
        <span style={{ color: item.priority === 1 ? 'green' : 'red' }}>{item.statusLabel}</span>
      ),
    },
  ];

  const componentReceiver = (column, childrens) => ({
    columnComponent: <E.TableTH>Duplicar</E.TableTH>,
    childrenComponent:
      childrens === null ? null : (
        <TableRow>
          <E.TableTD key={childrens.key} onClick={(e) => handleRowClick(childrens, e)}>
            <div style={{ marginLeft: '15px' }}>
              <Tooltip title={childrens.status === 'Duplicar card' ? 'Duplicando...' : 'Duplicar'}>
                <S.ButtonDetails
                  variant="contained"
                  className="duplicate-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyCards(childrens.key);
                  }}
                >
                  <MdContentCopy size={20} />
                </S.ButtonDetails>
              </Tooltip>
            </div>
          </E.TableTD>
        </TableRow>
      ),
  });

  const transformData = (data) =>
    data.map((item) => ({
      ...item,
      statusLabel: item.isPublished ? 'Ativo' : 'Inativo',
      priority: item.isPublished ? 1 : 0,
    }));

  const handleChangePage = (e, p) => {
    _DATA.jump(p);
    setPage(p);
  };

  const handleDeleteSelectedOrg = (orgIdToDelete) => {
    setSelectedOrgs((prev) => {
      const updatedOrgs = prev.filter((orgId) => orgId !== orgIdToDelete);
      cardFilter(search, activeOnly, updatedOrgs);
      return updatedOrgs;
    });
  };

  const handleCheck = (name) => (event) => {
    cardFilter(search, event.target.checked);
    if (name === 'activeOnly') setActiveOnly(event.target.checked);
  };

  const handleChangeSelectedOrgs = (event) => {
    const selected =
      typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
    setSelectedOrgs(selected);
    cardFilter(search, activeOnly, selected);
  };

  function cleanText(text) {
    if (!text) return '';
    return text
      .normalize('NFD') // Separa acentos das letras
      .replace(/[\u0300-\u036f]/g, '') // Remove os acentos
      .replace(/\s+/g, '') // Remove todos os espaços
      .toLowerCase();
  }

  const cardFilter = (search, ativos, orgSelecionada = []) => {
    let dataFiltrada = [...dataTotal].filter((card) => {
      const matchsearch =
        search.length === 0 ||
        cleanText(card.title).includes(cleanText(search)) ||
        cleanText(card.subtitle).includes(cleanText(search)) ||
        cleanText(card.keyWordsSet?.toString()).includes(cleanText(search));

      const matchAtivo =
        !ativos ||
        (card.isPublished && card.enableDate < Date.now() && card.disableDate > Date.now());

      const matchOrg =
        orgSelecionada.length === 0 ||
        (card.orgs && card.orgs.some((org) => orgSelecionada.includes(org)));

      return matchsearch && matchAtivo && matchOrg;
    });
    setDataSearch(dataFiltrada);
    setPage(1);
    _DATA.jump(1);
  };

  const copyCards = async (cardKey) => {
    try {
      const card = await getDataListRestApi(`Cards/${cardKey}`);
      const copyCardKey = firebaseDatabase.ref('Cards').push().key;

      card.cardId = copyCardKey;
      card.title += ' - Cópia';

      card.isDuplicating = true;

      await updateDatabase('Cards', card, copyCardKey, null);
      window.location.reload();
    } catch (err) {
      toast.error('Erro ao duplicar card.');
    }
  };

  const renderOrgs = (orgs) =>
    orgs.map((org, index) => `${organizations[org]?.name}${index === orgs.length - 1 ? '' : ','}`);

  return (
    <Route
      render={({ history }) => (
        <div style={{ maxHeight: '100%', width: '100%' }}>
          {/* Barra de busca e filtro */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              marginTop: 20,
              marginBottom: 20,
            }}
          >
            <S.BoxSearch>
              <S.InputBox>
                <S.Input
                  placeholder="Pesquisar um card..."
                  value={search}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearch(value);
                    cardFilter(value, activeOnly, selectedOrgs);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      cardFilter(search, activeOnly, selectedOrgs);
                    }
                  }}
                />
                <S.SearchIconButton type="button" onClick={(e) => e.preventDefault()}>
                  <img src={searchIcon} alt="" />
                </S.SearchIconButton>
              </S.InputBox>
            </S.BoxSearch>
            <Tooltip title={filterOpen ? 'Fechar filtro' : 'Filtro'} placement="top">
              <div
                onClick={(e) => {
                  setAnchorEl(e.currentTarget);
                  setFilterOpen((prev) => !prev);
                }}
                style={{
                  backgroundColor: '#f0f0f0',
                  padding: '8px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '40px',
                  width: '40px',
                }}
              >
                {filterOpen ? (
                  <X size={20} weight="fill" />
                ) : (
                  <FunnelSimple size={25} weight="fill" />
                )}
              </div>
            </Tooltip>
          </div>

          {/* Filtro por organização */}
          <Popper open={filterOpen} anchorEl={anchorEl} placement="bottom-start">
            <Paper sx={{ p: 2, mt: 1, width: 300 }}>
              <InputLabel>Filtrar por organização:</InputLabel>
              <FormControl fullWidth>
                <Select
                  multiple
                  value={selectedOrgs}
                  onChange={handleChangeSelectedOrgs}
                  input={<OutlinedInput />}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        marginTop: 4,
                        maxHeight: 300,
                      },
                    },
                  }}
                  renderValue={(selected) => (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        maxHeight: 80,
                        overflowY: 'auto',
                      }}
                    >
                      {selected.map((orgId) => (
                        <Chip
                          key={orgId}
                          onMouseDown={(e) => e.stopPropagation()}
                          label={organizations[orgId]?.name || orgId}
                          onDelete={(e) => {
                            e.stopPropagation();
                            handleDeleteSelectedOrg(orgId);
                          }}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {Object.keys(organizations)
                    .sort((a, b) => {
                      const nameA = organizations[a]?.name?.toLowerCase() || '';
                      const nameB = organizations[b]?.name?.toLowerCase() || '';
                      return nameA.localeCompare(nameB);
                    })
                    .map((orgId) => (
                      <MenuItem key={orgId} value={orgId}>
                        {organizations[orgId].name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <Checkbox checked={activeOnly} onChange={handleCheck('activeOnly')} />
              Mostrar apenas cards ativos
            </Paper>
          </Popper>

          {/* Tabela */}
          <div
            style={{
              maxHeight: '100%',
              width: '100%',
              position: 'relative',
              zIndex: 0,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {loading ? (
              <CircularProgress size={30} />
            ) : dataSearch.length > 0 ? (
              <>
                <CustomButtonsTable
                  data={_DATA.currentData()}
                  column={columns}
                  perPage={PER_PAGE}
                  componentReceiver={componentReceiver}
                  onRowClick={handleRowClick}
                  isPagination
                  loadingTableDisabled
                />
                <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                  <Pagination
                    count={count}
                    page={page}
                    variant="outlined"
                    shape="rounded"
                    color="secondary"
                    onChange={handleChangePage}
                  />
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', margin: '40px 0' }}>
                <strong>Nenhum resultado encontrado.</strong>
              </Box>
            )}
          </div>
        </div>
      )}
    />
  );
};

export default DataTableCards;

